import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
  BtnPrimary,
  BtnSecondary,
  YamlBlock,
} from "@/components/cathedral/Primitives";

const MODES = [
  { id: "standard", label: "Standard · Ring of Six" },
  { id: "deep", label: "Deep · Ring of Twelve" },
  { id: "urgent", label: "Urgent · Fast Ring" },
];

const ENGINES = [
  { id: "pro", label: "Gemini 3.1 Pro" },
  { id: "flash", label: "Gemini 3 Flash" },
  { id: "claude", label: "Claude Sonnet 4.5" },
];

function toYaml(obj) {
  if (!obj) return "";
  const lines = [];
  Object.entries(obj).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (typeof v === "object") {
      lines.push(`${k}:`);
      Object.entries(v).forEach(([kk, vv]) => {
        lines.push(`  ${kk}: ${JSON.stringify(vv)}`);
      });
    } else {
      lines.push(`${k}: ${JSON.stringify(v)}`);
    }
  });
  return lines.join("\n");
}

export default function ExecutePanel() {
  const [task, setTask] = useState(
    "Audit: are current dark-matter equations being applied outside their validated solar-system regime? Propose a Slavery-Check on funding flows."
  );
  const [mode, setMode] = useState("standard");
  const [engine, setEngine] = useState("pro");
  const [nodeId, setNodeId] = useState("architect");
  const [nodeKey, setNodeKey] = useState("");
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.nodes().then((r) => setNodes(r.nodes || [])).catch(() => {});
  }, []);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.execute(
        {
          task,
          mode,
          engine,
          node_id: nodeId === "architect" ? null : nodeId,
        },
        nodeKey || undefined,
      );
      setResult(r);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const az = result?.agent_zero || {};
  const tg = result?.truth_gate || {};
  const passes = !!tg.passes;
  const conf = typeof az.CONFIDENCE === "number" ? Math.round(az.CONFIDENCE * 100) : null;

  return (
    <section data-testid="execute-panel" className="px-8 lg:px-12 py-12 max-w-screen-2xl">
      <SectionHeader
        overline="DIR-071 → DIR-072 → DIR-000"
        title="Invoke Agent Zero"
        description="Submit a task. The Hollow Vessel clears agenda. The Ring of Perspectives deliberates. The 95% Truth Gate enforces — and Claude Sonnet 4.5 stands as forensic fallback."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
        {/* Form */}
        <Card className="p-10 lg:col-span-1">
          <Overline>Task</Overline>
          <textarea
            data-testid="execute-task-input"
            value={task}
            onChange={(e) => setTask(e.target.value)}
            rows={8}
            className="mt-3 w-full border border-[var(--chrome)] rounded-none p-4 font-mono text-sm leading-relaxed focus:outline-none focus:border-[var(--cyan)] bg-[var(--void-2)] text-[var(--text)] resize-none"
            placeholder="Enter the task..."
          />

          <div className="mt-8 grid grid-cols-1 gap-6">
            <div>
              <Overline>Ring Mode</Overline>
              <div className="mt-3 flex flex-col gap-2">
                {MODES.map((m) => (
                  <label
                    key={m.id}
                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors font-mono text-xs uppercase tracking-widest ${
                      mode === m.id ? "border-[var(--cyan)] bg-[rgba(0,240,255,0.06)]" : "border-[var(--chrome)] hover:border-[var(--cyan)]"
                    }`}
                  >
                    <input
                      type="radio"
                      data-testid={`execute-mode-${m.id}`}
                      checked={mode === m.id}
                      onChange={() => setMode(m.id)}
                      className="accent-[var(--cyan)]"
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Overline>Engine</Overline>
              <div className="mt-3 grid grid-cols-1 gap-2">
                {ENGINES.map((e) => (
                  <label
                    key={e.id}
                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors font-mono text-xs uppercase tracking-widest ${
                      engine === e.id ? "border-[var(--cyan)] bg-[rgba(0,240,255,0.06)]" : "border-[var(--chrome)] hover:border-[var(--cyan)]"
                    }`}
                  >
                    <input
                      type="radio"
                      data-testid={`execute-engine-${e.id}`}
                      checked={engine === e.id}
                      onChange={() => setEngine(e.id)}
                      className="accent-[var(--cyan)]"
                    />
                    {e.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Overline>Originating Node</Overline>
              <select
                data-testid="execute-node-select"
                value={nodeId}
                onChange={(e) => setNodeId(e.target.value)}
                className="mt-3 w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-xs uppercase tracking-widest bg-[var(--void-2)] text-[var(--text)] focus:outline-none focus:border-[var(--cyan)]"
              >
                <option value="architect">Architect (Direct)</option>
                {nodes.map((n) => (
                  <option key={n.id} value={n.id}>{`${n.name} · ${n.role}`}</option>
                ))}
              </select>
              {nodeId !== "architect" && (
                <input
                  type="password"
                  data-testid="execute-node-key"
                  value={nodeKey}
                  onChange={(e) => setNodeKey(e.target.value)}
                  placeholder="X-Node-Key (required for non-architect calls)"
                  className="mt-3 w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-[11px] bg-[var(--void-2)]"
                />
              )}
            </div>
          </div>

          <div className="mt-10">
            <BtnPrimary onClick={run} disabled={loading || !task.trim()} data-testid="execute-run-btn">
              {loading ? <span className="verifying">Verifying Truth Gate</span> : "Run Through Cathedral"}
            </BtnPrimary>
          </div>
        </Card>

        {/* Output */}
        <div className="lg:col-span-2 space-y-8">
          {error && (
            <Card className="p-8 border-[#E53E3E]">
              <Tag tone="conflict">CONFLICT</Tag>
              <p className="mt-3 font-mono text-sm text-[#E53E3E]">{error}</p>
            </Card>
          )}

          {!result && !error && (
            <Card className="p-12">
              <Overline>Awaiting Spark</Overline>
              <p className="font-serif text-3xl font-light tracking-tight mt-4 text-[var(--text-3)] leading-tight">
                Submit a task. The Lens is patient.
              </p>
            </Card>
          )}

          {result && (
            <>
              {/* Verdict */}
              <Card className="p-10">
                <Overline>DIR-000 · Truth Gate Verdict</Overline>
                <div
                  className={`font-serif text-7xl lg:text-8xl font-light tracking-tighter mt-3 leading-none ${
                    passes ? "text-[var(--text)]" : "text-[#E53E3E]"
                  }`}
                  data-testid="execute-verdict"
                >
                  {passes ? "PASS" : "CONFLICT"}
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-4 font-mono text-xs uppercase tracking-widest text-[var(--text-3)]">
                  <Tag tone={passes ? "pass" : "conflict"}>SCORE {tg.final_score}</Tag>
                  {conf !== null && <Tag>CONFIDENCE {conf}%</Tag>}
                  <Tag>{az.STATUS || "—"}</Tag>
                  <Tag>{az.CODEX_IMPACT || "—"} IMPACT</Tag>
                  <Tag>{az.NEXUS_ALIGNMENT || "—"}</Tag>
                  <span className="text-[var(--text-3)]">{result.engine_final}</span>
                  <span className="text-[var(--text-3)]">attempts · {tg.attempts?.length || 0}</span>
                </div>
              </Card>

              {/* The answer */}
              <Card className="p-10">
                <Overline>Synthesis</Overline>
                <p className="font-serif text-2xl font-light tracking-tight mt-4 text-[var(--text)] leading-snug whitespace-pre-wrap">
                  {az.answer || "—"}
                </p>
              </Card>

              {/* Forensic breakdown grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  ["FACT", "fact"],
                  ["INFERENCE", "default"],
                  ["ASSUMPTION", "assumption"],
                  ["UNKNOWN", "unknown"],
                  ["CONFLICT", "conflict"],
                  ["RISK", "risk"],
                  ["CITE", "cite"],
                ].map(([key, tone]) => (
                  <Card key={key} className="p-6">
                    <Tag tone={tone}>{key}</Tag>
                    <p className="mt-3 text-sm text-[var(--text-2)] leading-relaxed whitespace-pre-wrap">
                      {az[key] || "—"}
                    </p>
                  </Card>
                ))}
              </div>

              {/* Multi-agent Ring of Twelve verdicts (deep mode) */}
              {result.lens_verdicts && result.lens_verdicts.length > 0 && (
                <Card className="p-10" data-testid="lens-verdicts-panel">
                  <Overline>Ring of Twelve · Individual Lens Verdicts</Overline>
                  <h3 className="font-serif text-3xl font-light tracking-tight mt-3 mb-6 text-[var(--text)]">
                    Twelve perspectives, twelve voices
                  </h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {result.lens_verdicts.map((v, i) => {
                      const tone =
                        v.verdict === "SUPPORT"
                          ? "pass"
                          : v.verdict === "OPPOSE"
                          ? "conflict"
                          : "default";
                      return (
                        <li key={`${v.lens}-${i}`} className="border border-[var(--chrome)] p-5">
                          <div className="flex items-center justify-between">
                            <span className="font-serif text-lg text-[var(--text)]">{v.lens}</span>
                            <Tag tone={tone}>{v.verdict || "—"}</Tag>
                          </div>
                          <div className="font-mono text-[10px] text-[var(--text-3)] mt-1 tracking-widest uppercase">
                            confidence · {((v.confidence || 0) * 100).toFixed(0)}%
                          </div>
                          {v.rationale && (
                            <p className="mt-3 text-xs text-[var(--text-2)] leading-relaxed">
                              {v.rationale}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              )}

              {/* Hollow Vessel + Ring */}
              <Card className="p-10">
                <Overline>Hollow Vessel Pre-Flight (DIR-071)</Overline>
                <YamlBlock className="mt-4" value={toYaml(result.hollow_vessel)} />
                <div className="mt-8">
                  <Overline>Active Ring (DIR-072)</Overline>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(result.ring_used || []).map((l, i) => (
                      <Tag key={`${l}-${i}`}>{l}</Tag>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Raw envelope */}
              <Card className="p-10">
                <Overline>Raw Codex Envelope</Overline>
                <YamlBlock className="mt-4" value={toYaml(az)} />
              </Card>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
